// Shared constants + path helpers for the med-student "Combine Documents"
// feature (the green button in DoctorPortalBar's top bar).
//
// The combined day-view is a SYNTHETIC med-student document — it is NOT a real
// notes-c patient row. It lives at a stable per-day storage path and opens in
// MedStudentClinicPage via the sentinel uuid COMBINED_UUID (/clinic/combined).
//
// Why a synthetic row: MedStudentClinicPage + usePatientDocument derive the
// live-collab room name AND the storage path from the patient row via
// buildDocPath. By constructing ONE synthetic row here — used identically by
// the combine writer (MedStudentCombineButton) and by usePatientDocument's
// load branch — both sides resolve to the SAME path, so the collab server
// hydrates the room from the combined .json the button wrote. Keep this file
// the single source of truth for that row + path so the two never drift.

// Sentinel URL param. /clinic/<COMBINED_UUID>?date=YYYY-MM-DD opens the
// combined view for that day. usePatientDocument short-circuits on this value
// instead of doing a notes-c row lookup (there is no row).
export const COMBINED_UUID = 'combined';

// The synthetic patient row for the combined view. patient_name/EHR_ID are
// fixed sentinels chosen so buildDocPath's `${patient_name}-${EHR_ID||id}.json`
// yields `${date}-combined.json` — matching the legacy doctor-flow combine
// filename — regardless of which caller builds it.
export function buildCombinedPatientRow(date) {
  return {
    id: COMBINED_UUID,
    patient_name: date,     // -> filename `${date}-combined.json`
    EHR_ID: COMBINED_UUID,  // patientId = EHR_ID || id
    date_: date,
    type: '',
  };
}

// The combined document's storage path. Mirrors buildDocPath
// (usePatientDocument.js / MedStudentClinicPage.js) EXACTLY so the path the
// writer uploads to is the same one the collab room + hook resolve to.
export function buildCombinedDocPath(date, schema) {
  if (!date) return null;
  const row = buildCombinedPatientRow(date);
  const patientId = row.EHR_ID || row.id;
  const fileName = `${row.patient_name}-${patientId}.json`; // `${date}-combined.json`
  const year = date.split('-')[0];
  const schemaPrefix = schema && schema !== 'public' ? `${schema}/` : '';
  return `New/MedADocs/${schemaPrefix}${year}/${date}/${fileName}`;
}

// The collab server persists a `.yjs` CRDT snapshot as a sibling of the `.json`
// (backend api/collab/storage.py `yjs_path_for`). On room open it hydrates from
// `.yjs` FIRST and only falls back to `.json` when no `.yjs` exists — so
// re-running Combine must delete the `.yjs` to force re-hydration from the
// freshly written `.json`.
export function yjsPathFor(jsonPath) {
  if (!jsonPath) return null;
  return jsonPath.endsWith('.json') ? `${jsonPath.slice(0, -5)}.yjs` : `${jsonPath}.yjs`;
}
